import { Formik } from "formik";
import { useState } from "react";
import {
  Button,
  Form,
  Modal,
  ModalHeader,
  ModalTitle,
  Tab,
  Tabs,
} from "react-bootstrap";
import * as Yup from "yup";

import { Roles } from "../../constant/Roles";
import { AddFareZone } from "../../types/AddFareZone";
import { EditFareZone } from "../../types/EditFareZone";
import { FareZone } from "../../types/FareZone";
import { Review } from "../../types/Review";
import { useAuthContext } from "../auth/AuthContext";

import FareZoneService from "../../services/FareZoneService";
import { DoesUserHaveRole } from "../../services/LoggedInUserService";

import { useAlertContext } from "../alert/AlertContext";

import FareZoneFormDetailsTab from "./FareZoneFormDetailsTab";
import FareZoneFormFaresTab from "./FareZoneFormFaresTab";
import FareZoneFormOperatorsTab from "./FareZoneFormOperatorsTab";
import FareZoneFormSchemesTab from "./FareZoneFormSchemesTab";

export interface FareZoneFormProps {
  model: AddFareZone | EditFareZone;
  fareZone?: FareZone;
  onClose: () => void;
  onEditBoundary: (model: EditFareZone) => void;
  onSubmit: (id: string) => void;
  minEffectiveFrom: Date;
  suggestedEffectiveFrom: Date;
}

const FareZoneForm = ({
  model,
  fareZone,
  onClose,
  onEditBoundary,
  onSubmit,
  minEffectiveFrom,
  suggestedEffectiveFrom,
}: FareZoneFormProps) => {
  const fareZoneSchema = Yup.object({
    name: Yup.string().required("Please enter a name"),
    schemeCoordinatorId: Yup.string().required(
      "Please select the scheme coordinator"
    ),
    schemes: Yup.array().of(
      Yup.object({
        exclusions: Yup.string(),
        psCode: Yup.string().required("Please enter PS Code"),
        ticketName: Yup.string().required("Please enter a ticket name"),
        pbUrl: Yup.string()
          .required("Please enter a PB Url")
          .url("Please enter a valid Url"),
        schemeName: Yup.string().required("Please enter a scheme name"),
        stations: Yup.array()
          .of(
            Yup.object({
              nlc: Yup.number().required(),
              name: Yup.string().required(),
            })
          )
          .min(1, "Please select at least 1 station"),
      })
    ),
    operatorNOCs: Yup.array().min(1, "Please select at least 1 operator"),
    fare: Yup.object({
      effectiveFrom: Yup.date()
        .test(
          "effectiveFrom",
          "Please allow us 7 days to review your fare changes",
          (value) => {
            if (!value) return true;
            return value >= minEffectiveFrom;
          }
        )
        .required("Please provide the effective from date"),
      adultDailyFare: Yup.number()
        .min(0.01)
        .required("Please enter the daily fare for adults"),
      adultWeeklyFare: Yup.number()
        .nullable()
        .min(0.01, "Invalid price")
        .when("adultMonthlyFare", {
          is: (x?: number) => x,
          then: (schema) =>
            schema.required("Required if there is a monthly fare"),
          otherwise: (schema) => schema.optional(),
        }),
      adultMonthlyFare: Yup.number()
        .nullable()
        .min(0.01, "Invalid price")
        .when("adultQuarterlyFare", {
          is: (x?: number) => x,
          then: (schema) =>
            schema.required("Required if there is a quarterly fare"),
          otherwise: (schema) => schema.optional(),
        }),
      adultQuarterlyFare: Yup.number()
        .nullable()
        .min(0.01, "Invalid price")
        .when("adultAnnualFare", {
          is: (x?: number) => x,
          then: (schema) =>
            schema.required("Required if there is an annual fare"),
          otherwise: (schema) => schema.optional(),
        }),
      adultAnnualFare: Yup.number()
        .nullable()
        .min(0.01, "Invalid price")
        .optional(),
      childWeeklyFare: Yup.number()
        .nullable()
        .min(0.01, "Invalid price")
        .when("childMonthlyFare", {
          is: (x?: number) => x,
          then: (schema) =>
            schema.required("Required if there is a monthly fare"),
          otherwise: (schema) => schema.optional(),
        }),
      childMonthlyFare: Yup.number()
        .nullable()
        .min(0.01, "Invalid price")
        .when("childQuarterlyFare", {
          is: (x?: number) => x,
          then: (schema) =>
            schema.required("Required if there is a quarterly fare"),
          otherwise: (schema) => schema.optional(),
        }),
      childQuarterlyFare: Yup.number()
        .nullable()
        .min(0.01, "Invalid price")
        .when("childAnnualFare", {
          is: (x?: number) => x,
          then: (schema) =>
            schema.required("Required if there is an annual fare"),
          otherwise: (schema) => schema.optional(),
        }),
      childAnnualFare: Yup.number()
        .nullable()
        .min(0.01, "Invalid price")
        .optional(),
    }).default(undefined),
  });

  const alertContext = useAlertContext();
  const authContext = useAuthContext();
  const [tab, setTab] = useState("Details");

  const fareZoneService = new FareZoneService(alertContext.showAlert);
  const reviewMode =
    (fareZone?.isDraft && DoesUserHaveRole(authContext.user, Roles.Admin)) ??
    false;

  const submitForm = (model: AddFareZone | EditFareZone, actions: any) => {
    if (fareZone !== undefined) {
      // Edit Fare Zone
      fareZoneService
        .editFareZone(fareZone.id, model as EditFareZone)
        .then((success) => {
          if (success) {
            onSubmit(fareZone.id);
          }
        });
    } else {
      // Add Fare Zone
      fareZoneService.addFareZone(model as AddFareZone).then((newFareZone) => {
        if (newFareZone !== undefined) {
          onSubmit(newFareZone.id);
        }
      });
    }
  };

  const approve = () => {
    if (!fareZone) return;
    const review: Review = {
      lastModifiedDate: fareZone.lastModifiedDate,
    };
    fareZoneService.approve(fareZone.id, review).then(() => {
      onSubmit(fareZone.id);
    });
  };

  const reject = () => {
    if (!fareZone) return;
    const review: Review = {
      lastModifiedDate: fareZone.lastModifiedDate,
    };
    fareZoneService.approve(fareZone.id, review).then(() => {
      onSubmit(fareZone.id);
    });
  };

  const flattenedErrors = (errors: any): string[] => {
    const result: string[] = [];
    Object.values(errors).forEach((error) => {
      if (typeof error === "object") {
        const childErrors = flattenedErrors(error);
        result.push(...childErrors);
      } else if (error !== undefined) {
        result.push(error as string);
      }
    });
    return result;
  };

  const errorSummary = (errors: any) => {
    const e = flattenedErrors(errors);
    if (e.length === 0) return undefined;
    if (e.length === 1) return `${e[0]}`;
    return `${e[0]} + ${e.length - 1} more`;
  };

  return (
    <>
      <div
        className="modal show"
        style={{
          display: "block",
          position: "initial",
          backgroundColor: "rgba(255,255,255,0.5)",
        }}
      >
        <Formik
          initialValues={model}
          onSubmit={(values, action) =>
            submitForm(values as AddFareZone | EditFareZone, action)
          }
          validationSchema={fareZoneSchema}
        >
          {({ values, handleSubmit, errors, touched }) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Dialog size="lg">
                <ModalHeader closeButton onClick={onClose}>
                  <ModalTitle>
                    Fare Zone Details
                    {model.name && <span>- {model.name}</span>}
                  </ModalTitle>
                </ModalHeader>
                <Modal.Body
                  style={{
                    maxHeight: "calc(100vh - 340px)",
                    overflowY: "auto",
                  }}
                >
                  <Tabs
                    activeKey={tab}
                    onSelect={(k) => setTab(k as string)}
                    className="mb-3"
                  >
                    <Tab eventKey="Details" title="Details">
                      <FareZoneFormDetailsTab
                        currentFareZone={fareZone}
                        reviewMode={reviewMode}
                      />
                    </Tab>
                    <Tab eventKey="Fares" title="Fares">
                      <FareZoneFormFaresTab
                        currentFareZone={fareZone}
                        reviewMode={reviewMode}
                        minEffectiveFrom={minEffectiveFrom}
                        suggestedEffectiveFrom={suggestedEffectiveFrom}
                      />
                    </Tab>
                    <Tab eventKey={"Schemes"} title="Schemes">
                      <FareZoneFormSchemesTab
                        currentFareZone={fareZone}
                        reviewMode={reviewMode}
                      />
                    </Tab>
                    <Tab eventKey="Operators" title="Operators">
                      <FareZoneFormOperatorsTab
                        currentFareZone={fareZone}
                        reviewMode={reviewMode}
                      />
                    </Tab>
                  </Tabs>
                </Modal.Body>
                <Modal.Footer>
                  <div className="flex-grow-1">
                    {errors && touched && (
                      <p className="text-danger m-0">{errorSummary(errors)}</p>
                    )}
                  </div>

                  {fareZone !== undefined && (
                    <>
                      <Button variant="light" onClick={onClose}>
                        Close
                      </Button>
                      {!reviewMode && (
                        <Button
                          variant="secondary"
                          onClick={() => onEditBoundary(values as EditFareZone)}
                        >
                          Edit Boundary
                        </Button>
                      )}
                    </>
                  )}
                  {fareZone === undefined && (
                    <>
                      <Button variant="secondary" onClick={onClose}>
                        Back
                      </Button>
                    </>
                  )}
                  {!reviewMode && (
                    <Button variant="primary" type="submit">
                      Save changes
                    </Button>
                  )}
                  {reviewMode && (
                    <>
                      <Button variant="primary" onClick={approve}>
                        Approve
                      </Button>
                      <Button variant="danger" onClick={reject}>
                        Reject
                      </Button>
                    </>
                  )}
                </Modal.Footer>
              </Modal.Dialog>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default FareZoneForm;
